import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Stack, Card, Title, Text, Group, Button, Badge, Skeleton } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { sms } from '@repletoryreach/sdk-api';
import { formatDateTime, formatInteger, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const SmsIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M10.5 18h3" /><path d="M9 7.5h6M9 10.5h4" />
  </svg>
);

/**
 * Customer SMS at a glance (4 October 2026): whether it can be sent at all,
 * which provider sends and which one stands in, what the last day's sends came
 * to and when a provider last called back. The provider rows below hold the
 * keys; this card says whether the whole path works, and sends one message to
 * prove it.
 *
 * `nameOf` turns a provider code into the name on its card, so the routing
 * reads "BulkSMS" rather than the code.
 */
export function SmsCard({ nameOf }: { nameOf: (code: string) => string }) {
  const t = useT();
  const [to, setTo] = useState('');
  const [toError, setToError] = useState('');

  const statusQ = useQuery({
    queryKey: ['admin', 'sms-status'],
    queryFn: () => sms.adminStatus(),
    retry: false
  });
  // Anything that is not the status object (an old service, a stub answering a list) reads as no status,
  // so one card cannot blank the whole Integrations page.
  const raw = statusQ.data?.data;
  const status = raw && !Array.isArray(raw) && typeof raw === 'object' ? { ...raw, last_24_hours: raw.last_24_hours ?? [] } : null;

  const testM = useMutation({
    mutationFn: (number: string) => sms.adminTest({ to: number }),
    onSettled: () => { void statusQ.refetch(); }
  });
  const result = testM.data?.data ?? null;

  const statusLabel = (code: string) => t(`settings.integrations.sms.status.${code}`, code);

  function sendTest() {
    const number = to.trim();
    if (!/^\+[1-9]\d{6,14}$/.test(number)) {
      setToError(t('settings.integrations.sms.test.invalid'));
      return;
    }
    openConfirmModal({
      title: t('settings.integrations.sms.test.confirmTitle'),
      children: (
        <p style={{ margin: 0 }}>
          {t('settings.integrations.sms.test.confirmBody', {
            number,
            provider: status?.active_provider ? nameOf(status.active_provider) : t('settings.integrations.sms.none')
          })}
        </p>
      ),
      labels: { confirm: t('settings.integrations.sms.test.send'), cancel: t('action.cancel') },
      onConfirm: () => testM.mutate(number)
    });
  }

  const resultOk = result && (result.status === 'queued' || result.status === 'sent' || result.status === 'delivered');

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
            <span className="rr-integrations__tile" aria-hidden>{SmsIcon}</span>
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Title order={5}>{t('settings.integrations.sms.title')}</Title>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.integrations.sms.subtitle')}</Text>
            </Stack>
          </Group>
          {status && (
            <StatusBadge status={status.available ? 'healthy' : 'warning'}>
              {status.available ? t('settings.integrations.sms.available') : t('settings.integrations.sms.unavailable')}
            </StatusBadge>
          )}
        </Group>

        {statusQ.isPending && <Skeleton height={96} />}

        {statusQ.error && (
          <Alert color="error">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('settings.integrations.sms.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={() => { void statusQ.refetch(); }}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}

        {status && (
          <Stack gap="sm">
            {!status.available && (
              <Alert color="warning">{status.reason || t('settings.integrations.sms.unavailableHint')}</Alert>
            )}

            <Row label={t('settings.integrations.sms.active')}>
              {status.active_provider ? nameOf(status.active_provider) : t('settings.integrations.sms.none')}
            </Row>
            <Row label={t('settings.integrations.sms.backup')}>
              {status.backup_provider ? nameOf(status.backup_provider) : t('settings.integrations.sms.none')}
            </Row>
            <Row label={t('settings.integrations.sms.last24')}>
              {status.last_24_hours.length === 0
                ? t('settings.integrations.sms.noneSent')
                : (
                  <Group gap={4} wrap="wrap">
                    {status.last_24_hours.map((s) => (
                      <Badge key={s.status} size="xs" variant="light" color={s.status === 'failed' || s.status === 'refused' ? 'red' : s.status === 'delivered' ? 'green' : 'gray'}>
                        {t('settings.integrations.sms.statusCount', { status: statusLabel(s.status), count: formatInteger(s.count) })}
                      </Badge>
                    ))}
                  </Group>
                )}
            </Row>
            <Row label={t('settings.integrations.sms.lastWebhook')}>
              {status.last_webhook
                ? (
                  <span title={formatDateTime(status.last_webhook.received_at)}>
                    {t(status.last_webhook.outcome ? 'settings.integrations.sms.webhookWithOutcome' : 'settings.integrations.sms.webhook', {
                      provider: nameOf(status.last_webhook.provider_code),
                      when: formatRelative(status.last_webhook.received_at),
                      outcome: status.last_webhook.outcome ?? ''
                    })}
                  </span>
                )
                : t('settings.integrations.sms.noWebhook')}
            </Row>
          </Stack>
        )}

        <Group gap="sm" align="flex-start" wrap="wrap" className="rr-integrations__foot">
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <TextInput
              label={t('settings.integrations.sms.test.number')}
              description={t('settings.integrations.sms.test.numberHint')}
              placeholder="+27821234567"
              value={to}
              error={toError || undefined}
              onChange={(e) => { setTo(e.currentTarget.value); setToError(''); }}
              inputMode="tel"
            />
          </div>
          <Button
            size="sm"
            style={{ marginTop: 26 }}
            loading={testM.isPending}
            disabled={!status?.available || !to.trim()}
            onClick={sendTest}
          >
            {t('settings.integrations.sms.test.send')}
          </Button>
        </Group>

        {testM.error && (
          <Alert color="error">
            {testM.error instanceof Error ? testM.error.message : t('common.unknownError')}
          </Alert>
        )}
        {result && (
          <Alert color={resultOk ? 'success' : 'error'}>
            <Stack gap={2}>
              <Text role="bodySmall">
                {t('settings.integrations.sms.test.result', { status: statusLabel(result.status), segments: formatInteger(result.segments) })}
              </Text>
              {(result.error_code || result.error) && (
                <Text role="caption">
                  {[result.error_code, result.error].filter(Boolean).join(': ')}
                </Text>
              )}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Text role="caption" c="var(--rr-color-text-muted)" style={{ minWidth: 150 }}>{label}</Text>
      <Text role="bodySmall" as="div" style={{ minWidth: 0 }}>{children}</Text>
    </Group>
  );
}
